import {
  ArenaGameState,
  SeatPosition,
  Card,
  Direction,
  TrumpSuit,
  BidRecord,
  PlayedCard
} from './ArenaTypes';
import { getTeamForSeat, getNextSeat } from '../engine/gameConfigHelpers';

export interface OptimisticActionState {
  optimisticSeat: SeatPosition | null;
  optimisticReady: boolean | null;
  optimisticBid: BidRecord | null;
  optimisticTrump: { suit: TrumpSuit; direction?: Direction } | null;
  optimisticDiscards: string[] | null;
  optimisticPlay: { card: Card; seat: SeatPosition } | null;
  optimisticGameExtension?: Record<string, any> | null;
}

export interface PlayerIdentityContext {
  myPlayerId: string | null;
  myName: string;
  myPhotoUrl?: string | null;
  isTableCreatorOrHost: boolean;
}

/**
 * Computes authoritative base state for arena gameplay.
 * Pure pass-through mirroring server state.
 */
export function computeAuthoritativeBaseState(
  gameState: ArenaGameState | null
): ArenaGameState | null {
  return gameState;
}

/**
 * Reconciles optimistic actions against incoming base/server state, clearing actions
 * whose effect has been incorporated or whose valid phase has passed.
 */
export function reconcileOptimisticState(
  optimistic: OptimisticActionState,
  baseState: ArenaGameState | null,
  mySeat: SeatPosition | null,
  authoritativeMySeat: SeatPosition | null,
  gameStateSeats: ArenaGameState['seats'] | null | undefined
): OptimisticActionState {
  let {
    optimisticSeat,
    optimisticReady,
    optimisticBid,
    optimisticTrump,
    optimisticDiscards,
    optimisticPlay,
  } = optimistic;

  if (!baseState) {
    return {
      optimisticSeat,
      optimisticReady,
      optimisticBid,
      optimisticTrump,
      optimisticDiscards,
      optimisticPlay,
    };
  }

  // Clear optimistic bid once base state incorporates it or phase moves past BIDDING
  if (optimisticBid) {
    if (baseState.phase !== 'BIDDING') {
      optimisticBid = null;
    } else {
      const alreadyIncluded = (baseState.bids || []).some(
        b => b && b.seat === optimisticBid!.seat && (b.bid === optimisticBid!.bid || (b.isPass && optimisticBid!.isPass))
      );
      if (alreadyIncluded) {
        optimisticBid = null;
      }
    }
  }

  // Clear optimistic trump declaration once base state transitions past DECLARE_TRUMP
  if (optimisticTrump && baseState.phase !== 'DECLARE_TRUMP') {
    optimisticTrump = null;
  }

  // Clear optimistic kitty discards once base state resets (DEALING, LOBBY, HAND_END, GAME_OVER)
  if (optimisticDiscards) {
    const isResetPhase = baseState.phase === 'LOBBY' || baseState.phase === 'HAND_END' || baseState.phase === 'GAME_OVER' || baseState.phase === 'DEALING';
    if (isResetPhase) {
      optimisticDiscards = null;
    }
  }

  // Clear optimistic card play once base state incorporates it or trick resolves
  if (optimisticPlay) {
    if (baseState.phase !== 'PLAYING' && baseState.phase !== 'TRICK_RESOLVED' && baseState.phase !== 'TRICK_END') {
      optimisticPlay = null;
    } else {
      const alreadyInCurrentTrick = (baseState.currentTrick || []).some(
        t => t && t.card && t.card.id === optimisticPlay!.card.id
      );
      const alreadyInCompletedTricks = (baseState.completedTricks || []).some(
        t => t && t.cards && t.cards.some((c: any) => (c?.card?.id === optimisticPlay!.card.id || c?.id === optimisticPlay!.card.id))
      );
      if (alreadyInCurrentTrick || alreadyInCompletedTricks) {
        optimisticPlay = null;
      }
    }
  }

  // Reconcile optimistic seat when authoritative state catches up
  if (optimisticSeat !== null && authoritativeMySeat === optimisticSeat) {
    optimisticSeat = null;
  }

  // Reconcile optimistic ready status when authoritative state catches up
  if (optimisticReady !== null && mySeat !== null && gameStateSeats) {
    const authoritativePlayer = gameStateSeats[mySeat];
    if (authoritativePlayer && Boolean(authoritativePlayer.isReady) === optimisticReady) {
      optimisticReady = null;
    }
  }

  // Reset optimistic lobby states if transitioning away from LOBBY
  if (baseState.phase && baseState.phase !== 'LOBBY') {
    optimisticSeat = null;
    if (baseState.phase !== 'HAND_END' && baseState.phase !== 'GAME_OVER') {
      optimisticReady = null;
    }
  }

  return {
    optimisticSeat,
    optimisticReady,
    optimisticBid,
    optimisticTrump,
    optimisticDiscards,
    optimisticPlay,
  };
}

/**
 * Computes the derived effective private hand with optimistic discards and plays filtered out.
 * Hand completion is strictly governed by authoritative server state.
 */
export function computeEffectivePrivateHand(
  myPrivateHand: Card[],
  activeState: ArenaGameState | null,
  optimistic: OptimisticActionState,
  mySeat: SeatPosition | null,
  gameState: ArenaGameState | null
): Card[] {
  // gameState.phase is authoritative truth for hand lifecycle
  const currentPhase = gameState?.phase ?? activeState?.phase;
  const isSetupPhase = currentPhase === 'DEALING' || currentPhase === 'BIDDING' || currentPhase === 'PASSING' || currentPhase === 'DECLARE_TRUMP' || currentPhase === 'KITTY_DISCARD';
  const isHandCompleted = (
    currentPhase === 'HAND_END' ||
    currentPhase === 'GAME_OVER' ||
    currentPhase === 'LOBBY' ||
    (!isSetupPhase && (Boolean(gameState?.isHandComplete) || Boolean(activeState?.isHandComplete)))
  );

  if (isHandCompleted) {
    return [];
  }

  const maxTricks = 13;
  let hand = myPrivateHand;
  if (isSetupPhase && hand.length < maxTricks && mySeat !== null && (gameState?.hands?.[mySeat]?.length || 0) >= maxTricks) {
    hand = gameState!.hands[mySeat]!;
  }

  if (optimistic.optimisticDiscards && optimistic.optimisticDiscards.length > 0) {
    hand = hand.filter(c => !optimistic.optimisticDiscards!.includes(c.id));
  }
  if (optimistic.optimisticPlay) {
    hand = hand.filter(c => c.id !== optimistic.optimisticPlay!.card.id);
  }

  // Filter out cards that already exist in currentTrick or completedTricks during active trick play
  if (!isSetupPhase && (currentPhase === 'PLAYING' || currentPhase === 'TRICK_END' || currentPhase === 'TRICK_RESOLVED')) {
    if (activeState?.kittyDiscards && activeState.kittyDiscards.length > 0 && hand.length > maxTricks) {
      const discardIds = new Set(activeState.kittyDiscards.map(c => c.id));
      hand = hand.filter(c => !discardIds.has(c.id));
    }
    const playedCardIds = new Set<string>();
    for (const t of activeState?.currentTrick || []) {
      if (t?.card?.id) playedCardIds.add(t.card.id);
    }
    for (const ct of activeState?.completedTricks || []) {
      for (const t of ct?.cards || []) {
        if (t?.card?.id) playedCardIds.add(t.card.id);
      }
    }
    if (playedCardIds.size > 0) {
      hand = hand.filter(c => !playedCardIds.has(c.id));
    }
  }

  return hand;
}

/**
 * Computes effective game state by taking baseState and applying optimistic visual overlays.
 * Never calculates winners, scores, or rule outcomes—strictly preserves server authority.
 */
export function computeEffectiveGameState(
  baseState: ArenaGameState | null,
  optimistic: OptimisticActionState,
  identity: PlayerIdentityContext,
  mySeat: SeatPosition | null
): ArenaGameState | null {
  if (!baseState) return null;

  let effective: ArenaGameState = { ...baseState };

  // Optimistic Seat Selection & Claiming
  if (optimistic.optimisticSeat !== null && effective.seats) {
    const updatedSeats = [...effective.seats];
    for (let i = 0; i < 4; i++) {
      if (i !== optimistic.optimisticSeat && updatedSeats[i]) {
        const s = updatedSeats[i];
        if (
          (identity.myPlayerId && s?.memberId === identity.myPlayerId) ||
          s?.name === identity.myName
        ) {
          updatedSeats[i] = null;
        }
      }
    }
    const existingAtTarget = updatedSeats[optimistic.optimisticSeat];
    const isHostPlayer = identity.isTableCreatorOrHost || Boolean(existingAtTarget?.isHost) || (optimistic.optimisticSeat === 0 && !updatedSeats.some((s, idx) => idx !== 0 && s?.isHost));
    updatedSeats[optimistic.optimisticSeat] = {
      name: identity.myName,
      avatarUrl: identity.myPhotoUrl || undefined,
      memberId: identity.myPlayerId || undefined,
      seat: optimistic.optimisticSeat,
      team: getTeamForSeat(optimistic.optimisticSeat),
      isBot: false,
      isHost: isHostPlayer,
      isReady: optimistic.optimisticReady !== null ? optimistic.optimisticReady : Boolean(existingAtTarget?.isReady),
      cardCount: existingAtTarget?.cardCount || 0,
      booksWon: existingAtTarget?.booksWon || 0,
    };
    effective.seats = updatedSeats;
  }

  // Optimistic Ready Status
  if (optimistic.optimisticReady !== null && mySeat !== null && effective.seats) {
    const updatedSeats = [...effective.seats];
    if (updatedSeats[mySeat]) {
      updatedSeats[mySeat] = {
        ...updatedSeats[mySeat]!,
        isReady: optimistic.optimisticReady,
      };
      effective.seats = updatedSeats;
    }
  }

  // Optimistic Bidding & Passing: append the local player's bid to bids list for immediate UI feedback.
  // The server remains authoritative for phase transitions, winning bidder, and contract outcomes.
  if (optimistic.optimisticBid && effective.phase === 'BIDDING') {
    const alreadyIncluded = (effective.bids || []).some(
      b => b && b.seat === optimistic.optimisticBid!.seat && (b.bid === optimistic.optimisticBid!.bid || (b.isPass && optimistic.optimisticBid!.isPass))
    );
    if (!alreadyIncluded) {
      const updatedBids = [...(effective.bids || []), optimistic.optimisticBid];
      const nextBidderSeat = ((optimistic.optimisticBid.seat + 1) % 4) as SeatPosition;

      effective = {
        ...effective,
        bids: updatedBids,
        currentBidderSeat: nextBidderSeat,
        currentTurnSeat: nextBidderSeat,
        turnStartedAt: Date.now(),
        updatedAt: Date.now()
      };
    }
  }

  // Optimistic Trump Declaration
  if (optimistic.optimisticTrump && effective.phase === 'DECLARE_TRUMP') {
    const winnerSeat = effective.winningBidderSeat ?? effective.currentTurnSeat ?? 0;
    let updatedHands = effective.hands;
    if (effective.kitty && effective.kitty.length > 0 && updatedHands?.[winnerSeat]) {
      const currentHand = updatedHands[winnerSeat];
      const handCardIds = new Set(currentHand.map(c => c.id));
      const newKitty = effective.kitty.filter(c => !handCardIds.has(c.id));
      updatedHands = {
        ...updatedHands,
        [winnerSeat]: [...currentHand, ...newKitty]
      };
    }
    effective = {
      ...effective,
      trumpSuit: optimistic.optimisticTrump.suit,
      direction: optimistic.optimisticTrump.direction || effective.direction,
      isNoTrump: optimistic.optimisticTrump.suit === 'NT' || optimistic.optimisticTrump.suit === 'NoTrump' || effective.isNoTrump,
      hands: updatedHands,
      kittyRevealedToWinner: true,
      phase: 'KITTY_DISCARD',
      turnStartedAt: Date.now(),
      updatedAt: Date.now()
    };
  }

  // Optimistic Kitty Discards
  if (optimistic.optimisticDiscards && effective.phase === 'KITTY_DISCARD') {
    const winnerSeat = effective.winningBidderSeat;
    const winnerHand = (winnerSeat !== null && effective.hands) ? effective.hands[winnerSeat] : null;
    const kittyCount = effective.kitty?.length || 6;
    const matchingCount = (winnerHand || []).filter(c => optimistic.optimisticDiscards!.includes(c.id)).length;
    if (matchingCount === kittyCount && winnerHand && winnerHand.length >= kittyCount) {
      let updatedHands = {
        ...effective.hands,
        [winnerSeat!]: winnerHand.filter(c => !optimistic.optimisticDiscards!.includes(c.id))
      };
      effective = {
        ...effective,
        hands: updatedHands,
        phase: 'PLAYING',
        currentTurnSeat: effective.winningBidderSeat ?? 0,
        turnStartedAt: Date.now(),
        updatedAt: Date.now()
      };
    }
  }

  // Optimistic Card Play: append the local player's card to currentTrick for immediate visual feedback.
  // The server remains authoritative for trick resolution, trickWinnerSeat, and book awarding.
  if (optimistic.optimisticPlay && effective.phase === 'PLAYING') {
    const alreadyInCurrent = (effective.currentTrick || []).some(
      t => t && t.card && t.card.id === optimistic.optimisticPlay!.card.id
    );
    const alreadyInCompleted = (effective.completedTricks || []).some(
      t => t && t.cards && t.cards.some((c: any) => (c?.card?.id === optimistic.optimisticPlay!.card.id || c?.id === optimistic.optimisticPlay!.card.id))
    );

    if (!alreadyInCurrent && !alreadyInCompleted) {
      const newPlayedCard: PlayedCard = {
        card: optimistic.optimisticPlay.card,
        seat: optimistic.optimisticPlay.seat,
        timestamp: Date.now()
      };
      const updatedTrick = [...(effective.currentTrick || []), newPlayedCard];
      const isTrickComplete = updatedTrick.length >= 4;

      // Only advance currentTurnSeat optimistically if trick is still in progress (< 4 cards)
      // When trick reaches 4 cards, server authoritatively determines trick winner and next turn.
      let nextTurnSeat = effective.currentTurnSeat;
      if (!isTrickComplete) {
        nextTurnSeat = getNextSeat(optimistic.optimisticPlay.seat);
      }

      let updatedHands = effective.hands;
      if (updatedHands && updatedHands[optimistic.optimisticPlay.seat]) {
        updatedHands = {
          ...updatedHands,
          [optimistic.optimisticPlay.seat]: updatedHands[optimistic.optimisticPlay.seat].filter(
            c => c.id !== optimistic.optimisticPlay!.card.id
          )
        };
      }

      // Optimistic Hearts Broken: If playing a Heart or Queen of Spades in Hearts, break hearts immediately
      let updatedHeartsBroken = effective.heartsBroken;
      const isHeartsGame = effective.cardGameType === 'HEARTS' || effective.gameType === 'HEARTS';
      if (isHeartsGame && !updatedHeartsBroken) {
        const c = optimistic.optimisticPlay.card;
        if (
          c &&
          (c.suit === 'H' ||
            c.id?.startsWith('H_') ||
            c.id === 'S_Q' ||
            c.id === 'QS' ||
            (c.suit === 'S' && c.rank === 'Q'))
        ) {
          updatedHeartsBroken = true;
        }
      }

      effective = {
        ...effective,
        currentTrick: updatedTrick,
        currentTurnSeat: nextTurnSeat,
        hands: updatedHands,
        heartsBroken: updatedHeartsBroken,
        turnStartedAt: Date.now(),
        updatedAt: Date.now()
      };
    }
  }

  if (optimistic.optimisticGameExtension) {
    effective = {
      ...effective,
      gameExtension: {
        ...(effective.gameExtension || {}),
        ...optimistic.optimisticGameExtension
      } as any
    };
  }

  return effective;
}

